import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Send, X } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { QuickReplies } from './QuickReplies';
import { useAICoach } from '../../hooks/useAICoach';
import { useHealthMetrics } from '../../hooks/useHealthMetrics';
import { generateSuggestions } from '../../utils/chat/suggestionGenerator';

interface AIChatProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialMessage?: string;
}

export function AIChat({ isOpen: externalIsOpen, onOpenChange, initialMessage }: AIChatProps) {
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const metrics = useHealthMetrics();
  
  const { 
    messages, 
    isTyping, 
    sendMessage, 
    clearMessages 
  } = useAICoach({
    onTypingComplete: () => {
      scrollToBottom();
      setShowSuggestions(true);
    }
  });

  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setExpanded(externalIsOpen);
    }
  }, [externalIsOpen]);

  // Send welcome message when chat opens
  useEffect(() => {
    if (expanded && messages.length === 0) {
      const timer = setTimeout(() => {
        sendMessage('', true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [expanded, messages.length, sendMessage]);

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTo({
        top: chatWindowRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleClose = () => {
    setExpanded(false);
    onOpenChange?.(false);
    clearMessages();
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    
    const message = input.trim();
    setInput('');
    setShowSuggestions(false);
    await sendMessage(message, false);
  };

  const handleQuickReply = (reply: QuickReply) => {
    if (isTyping) return;
    setInput('');
    setShowSuggestions(false);
    sendMessage(reply.text, false);
  };

  return (
    <AnimatePresence>
      {expanded && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="theme-card w-full max-w-md relative overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between bg-background/50 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Bot className="w-5 h-5 text-primary" />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </div>
                <div>
                  <div className="font-medium">Drogo</div>
                  <div className="text-xs text-muted-foreground">AI Health Coach</div>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div 
              ref={chatWindowRef}
              className="h-[60vh] overflow-y-auto bg-gradient-to-b from-background/50 to-background p-4 theme-scrollbar"
            >
              <AnimatePresence mode="popLayout">
                {messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    content={message.content}
                    type={message.type}
                    isTyping={message.isTyping}
                    messageId={message.id}
                  />
                ))}
              </AnimatePresence>

              {/* Quick Replies */}
              {showSuggestions && !isTyping && messages.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="pt-4"
                >
                  <QuickReplies
                    replies={generateSuggestions(metrics)}
                    onSelect={handleQuickReply}
                  />
                </motion.div>
              )}
            </div>

            {/* Input */}
            <ChatInput
              value={input}
              onChange={setInput}
              onSend={handleSend}
              isTyping={isTyping}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}